import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import SearchableDropdown from '../components/SearchableDropdown';
import { useToast } from '../contexts/ToastContext';
import { staffService } from '../services/staffService';
import { classService } from '../services/classService';
import { designationService } from '../services/designationService';
import { fieldConfigService } from '../services/fieldConfigService';
import APIRequest from '../api/APIRequest';
import { useSelector } from 'react-redux';
import DynamicFields from '../components/forms/DynamicFields';
import CustomDatePicker from '../components/ui/CustomDatePicker';
import PhoneInput from '../components/ui/PhoneInput';

const formatPhoneForInput = (phone) => {
  if (!phone) return '+91 ';
  if (phone.startsWith('+')) {
    if (phone.includes(' ')) return phone;
    const match = phone.match(/^(\+\d{1,3})(\d+)$/);
    if (match) return `${match[1]} ${match[2]}`;
    return phone;
  }
  return `+91 ${phone}`;
};


// Hardcoded list removed in favor of dynamic DB-driven fields

const SYSTEM_FIELDS = [
  'name', 'email', 'phone', 'dateOfBirth', 'gender', 'maritalStatus', 
  'motherTongue', 'nationality', 'aadharNumber', 'pan', 'bloodGroup', 
  'address', 'salary', 'contactPerson', 'emergencyContacts', 'subjects',
  'class', 'academicYear', 'inChargeOf', 'employeeId', 'employeeNo', 'designation', 'qualification', 'experienceInYears'
];

const StaffEdit = ({ teacherData: propTeacherData }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [teacherData, setTeacherData] = useState(propTeacherData);
  const [classes, setClasses] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(!propTeacherData);
  const [saving, setSaving] = useState(false);
  const [validationErrors] = useState({});
  const [configFields, setConfigFields] = useState([]); // Fields fetched for current designation
  const [imagePreview, setImagePreview] = useState(null);
  const { user } = useSelector((state) => state.auth);

  const [formData, setFormData] = useState({
    name: '',
    designation: 'Teacher',
    qualification: '',
    experienceInYears: 0,
    email: '',
    phone: '',
    class: '',
    dateOfBirth: '',
    contactPerson: [{ relation: '', phone: '', name: '' }],
    maritalStatus: 'single',
    gender: 'male',
    motherTongue: '',
    nationality: '',
    subjects: [{ name: '' }],
    aadharNumber: '',
    pan: '',
    bloodGroup: '',
    address: '',
    salary: '',
    academicYear: '',
    employeeId: '',
    inChargeOf: '',
    password: '',
    employeeNo: '',
    customFields: {}
  });

  const deriveYearFromClass = (classIdOrObj) => {
    if (!classIdOrObj || !classes.length) return '';
    const classId = classIdOrObj?._id || classIdOrObj;
    const cls = classes.find(c => c._id === classId);
    if (cls && cls.academicYearFrom && cls.academicYearTo) {
      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      return `${fromYear}-${toYear}`;
    }
    return '';
  };

  const getUniqueAcademicYears = () => {
    const academicYears = new Set();
    classes.forEach(cls => {
      if (cls.academicYearFrom && cls.academicYearTo) {
        const fromYear = cls.academicYearFrom.toString().length <= 4 
          ? cls.academicYearFrom 
          : new Date(cls.academicYearFrom).getFullYear();
        const toYear = cls.academicYearTo.toString().length <= 4 
          ? cls.academicYearTo 
          : new Date(cls.academicYearTo).getFullYear();
        academicYears.add(`${fromYear}-${toYear}`);
      }
    });
    return Array.from(academicYears).sort();
  };

  const getFilteredClasses = () => {
    if (!formData.academicYear) return classes;
    
    return classes.filter(cls => {
      if (!cls.academicYearFrom || !cls.academicYearTo) return false;
      
      const fromYear = cls.academicYearFrom.toString().length <= 4 
        ? cls.academicYearFrom 
        : new Date(cls.academicYearFrom).getFullYear();
      const toYear = cls.academicYearTo.toString().length <= 4 
        ? cls.academicYearTo 
        : new Date(cls.academicYearTo).getFullYear();
      const classAcademicYear = `${fromYear}-${toYear}`;
      
      if (classAcademicYear !== formData.academicYear) return false;

      // Filter: Show only classes without an in-charge OR the class this teacher is already in-charge of
      return !cls.classIncharge || cls.classIncharge?._id === id || cls.classIncharge === id;
    });
  };

  useEffect(() => {
    // If teacher data is passed as props, use it directly
    if (propTeacherData) {
      setTeacherData(propTeacherData);
        setFormData({
          name: propTeacherData.name || '',
          designation: propTeacherData.designation || 'Teacher',
          qualification: propTeacherData.qualification || '',
          experienceInYears: propTeacherData.experienceInYears || 0,
          email: propTeacherData.email || '',
          phone: formatPhoneForInput(propTeacherData.phone),
          dateOfBirth: propTeacherData.dateOfBirth || '',
          contactPerson: (propTeacherData.contactPerson || [{ relation: '', phone: '', name: '' }]).map(cp => ({
            ...cp,
            phone: formatPhoneForInput(cp.phone)
          })),
          maritalStatus: propTeacherData.maritalStatus || 'single',
          gender: propTeacherData.gender || 'male',
          motherTongue: propTeacherData.motherTongue || '',
          nationality: propTeacherData.nationality || '',
          subjects: propTeacherData.subjects || [{ name: '' }],
          aadharNumber: propTeacherData.aadharNumber || '',
          pan: propTeacherData.pan || '',
          bloodGroup: propTeacherData.bloodGroup || '',
          address: propTeacherData.address || '',
          salary: propTeacherData.salary || '',
          academicYear: propTeacherData.academicYear || '',
          employeeId: propTeacherData.employeeId || '',
          inChargeOf: propTeacherData.inChargeOf || propTeacherData.class || '',
          password: '',
          employeeNo: propTeacherData.employeeNo || '',
          customFields: propTeacherData.customFields || {}
        });
      setLoading(false);
      return;
    }

    // Otherwise, fetch teacher data
    const fetchTeacherData = async () => {
      setLoading(true);
      try {
        const api = new APIRequest();
        const response = await api.request(`/admin/getTeacher?id=${id}`, 'GET', {}, user.token);
        
        if (response.success) {
          setTeacherData(response.teacher);
          setFormData({
            name: response.teacher.name || '',
            designation: response.teacher.designation || 'Mother Teacher',
            qualification: response.teacher.qualification || '',
            experienceInYears: response.teacher.experienceInYears || 0,
            email: response.teacher.email || '',
            phone: formatPhoneForInput(response.teacher.phone),
            dateOfBirth: response.teacher.dateOfBirth || '',
            contactPerson: (response.teacher.contactPerson || [{ relation: '', phone: '', name: '' }]).map(cp => ({
              ...cp,
              phone: formatPhoneForInput(cp.phone)
            })),
            maritalStatus: response.teacher.maritalStatus || 'single',
            gender: response.teacher.gender || 'male',
            motherTongue: response.teacher.motherTongue || '',
            nationality: response.teacher.nationality || '',
            subjects: response.teacher.subjects || [{ name: '' }],
            aadharNumber: response.teacher.aadharNumber || '',
            pan: response.teacher.pan || '',
            bloodGroup: response.teacher.bloodGroup || '',
            address: response.teacher.address || '',
            salary: response.teacher.salary || '',
            academicYear: response.teacher.academicYear || '',
            employeeId: response.teacher.employeeId || '',
            inChargeOf: response.teacher.inChargeOf || response.teacher.class || '',
            password: '',
            employeeNo: response.teacher.employeeNo || '',
            customFields: response.teacher.customFields || {}
          });
        } else {
          error(response.message || 'Failed to fetch teacher details');
        }
      } catch (err) {
        console.error('Error fetching teacher data:', err);
        error('Failed to fetch teacher details');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchTeacherData();
    }
  }, [id, user.token, error, propTeacherData]);

  // Fetch classes data
  useEffect(() => {
    const fetchClasses = async () => {
    try {
      const classData = await classService.getAllClasses(user.token);
      setClasses(classData);
    } catch (err) {
      console.error('Error loading classes:', err);
    }
  };
    fetchClasses();
  }, [user.token]);

  // Auto-derive academic year if missing
  useEffect(() => {
    if (formData.inChargeOf && !formData.academicYear && classes.length > 0) {
      const derivedYear = deriveYearFromClass(formData.inChargeOf);
      if (derivedYear) {
        setFormData(prev => ({ ...prev, academicYear: derivedYear }));
      }
    }
  }, [formData.inChargeOf, classes, formData.academicYear, deriveYearFromClass]);

  // Fetch designations on component mount
  useEffect(() => {
    const loadDesignations = async () => {
      try {
        const response = await designationService.getAllDesignations(user.token);
        const data = Array.isArray(response) ? response : (response.data || []);
        setDesignations(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error('Error loading designations:', err);
      }
    };
    loadDesignations();
  }, [user.token]);

  // Fetch custom fields when designation changes
  useEffect(() => {
    const fetchFields = async () => {
      if (!formData.designation || designations.length === 0) {
        setConfigFields([]);
        return;
      }
      
      const selectedDesignation = designations.find(d => d.name === formData.designation);
      if (selectedDesignation) {
        try {
          const response = await fieldConfigService.getDesignationFields(selectedDesignation._id, user.token);
          const fields = Array.isArray(response) ? response : (response.data || []);
          setConfigFields(fields);
        } catch (err) {
          console.error('Error fetching fields for designation:', err);
          setConfigFields([]);
        }
      } else {
        setConfigFields([]);
      }
    };
    fetchFields();
  }, [formData.designation, designations, user.token]);

  // Synchronize inChargeOf when classes or teacherData change (Run only once on init or when data arrives)
  useEffect(() => {
    if (classes.length > 0 && teacherData?._id && !formData.inChargeOf) {
      // 1. Try to find class where teacher is incharge (check both ID and object)
      const inchargedClass = classes.find(cls => 
        (cls.classIncharge?._id === teacherData._id) || 
        (cls.classIncharge === teacherData._id)
      );
      
      if (inchargedClass) {
        setFormData(prev => ({
          ...prev,
          inChargeOf: inchargedClass
        }));
      }
    }
    // Only depend on initialization markers (classes + raw data)
  }, [classes, teacherData?._id]);

  const handleCustomFieldChange = (fieldName, value) => {
    const fieldConfig = configFields.find(f => f.fieldName === fieldName);
    const isSystem = SYSTEM_FIELDS.includes(fieldName) || fieldConfig?.isSystemField;
    if (isSystem) {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        customFields: {
          ...prev.customFields,
          [fieldName]: value
        }
      }));
    }
  };


  // Create class options for dropdown based on filtered classes

  const addContactPerson = () => {
    setFormData({
      ...formData,
      contactPerson: [...formData.contactPerson, { relation: '', phone: '', name: '' }]
    });
  };

  const removeContactPerson = (index) => {
    const updated = formData.contactPerson.filter((_, i) => i !== index);
    setFormData({ ...formData, contactPerson: updated });
  };

  const updateContactPerson = (index, field, value) => {
    const updated = formData.contactPerson.map((contact, i) => 
      i === index ? { ...contact, [field]: value } : contact
    );
    setFormData({ ...formData, contactPerson: updated });
  };

  const addSubject = () => {
    setFormData({
      ...formData,
      subjects: [...formData.subjects, { name: '' }]
    });
  };

  const removeSubject = (index) => {
    const updated = formData.subjects.filter((_, i) => i !== index);
    setFormData({ ...formData, subjects: updated });
  };

  const updateSubject = (index, value) => {
    const updated = formData.subjects.map((s, i) => 
      i === index ? { name: value } : s
    );
    setFormData({ ...formData, subjects: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const payload = new FormData();
    payload.append('name', formData.name);
    payload.append('designation', formData.designation);
    
    // Consolidate qualification and experience
    const qualification = formData.qualification || formData.customFields?.qualification || '';
    const experienceInYears = (formData.experienceInYears !== '' && formData.experienceInYears !== undefined) 
      ? formData.experienceInYears 
      : (formData.customFields?.experienceInYears || 0);

    payload.append('qualification', qualification);
    payload.append('experienceInYears', experienceInYears);
    payload.append('email', formData.email);
    payload.append('phone', formData.phone);
    payload.append('dateOfBirth', formData.dateOfBirth);
    payload.append('contactPerson', JSON.stringify(formData.contactPerson));
    payload.append('maritalStatus', formData.maritalStatus);
    payload.append('gender', formData.gender);
    payload.append('motherTongue', formData.motherTongue);
    payload.append('nationality', formData.nationality);
    payload.append('subjects', JSON.stringify(formData.subjects));
    payload.append('aadharNumber', formData.aadharNumber);
    payload.append('pan', formData.pan);
    payload.append('bloodGroup', formData.bloodGroup);
    payload.append('address', formData.address);
    payload.append('salary', formData.salary);
    payload.append('role', 'staff');
    payload.append('academicYear', formData.academicYear);
    if (formData.password) {
        payload.append('password', formData.password);
    }
    payload.append('inChargeOf', formData.inChargeOf?._id || '');
    payload.append('employeeNo', formData.employeeNo || '');
    payload.append('customFields', JSON.stringify(formData.customFields));

    if (formData.image instanceof File) {
        payload.append('image', formData.image);
    }

    try {
      const response = await staffService.updateTeacher(id, payload, user.token);
      if (response.success) {
        success('Staff updated successfully!');
        navigate(`/staff/${id}`);
      } else {
        error(response.message || 'Failed to update staff. Please try again.');
      }
    } catch (err) {
      console.error('Error updating staff:', err);
      error('Failed to update staff. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleBackToList = () => {
    navigate('/staff');
  };

  const [activeTab, setActiveTab] = useState('personal');

  const tabs = [
    { id: 'personal', label: 'Personal Information' },
    { id: 'professional', label: 'Professional Details' },
    { id: 'additional', label: 'Additional Information' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50 -m-6 pb-12">
      {/* Cover Header */}
      <div className="h-32 bg-gradient-to-r from-emerald-600 to-teal-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-white/[0.1] bg-[size:20px_20px]" />
        
        <div className="absolute inset-0 flex items-center justify-center">
          <h1 className="text-2xl font-black text-white/90 tracking-tight uppercase">Edit Staff Profile</h1>
        </div>

        <button
          onClick={handleBackToList}
          className="absolute top-6 left-6 p-2 bg-white/20 hover:bg-white/30 text-white rounded-lg transition-all backdrop-blur-md flex items-center gap-2 group z-10"
        >
          <ArrowLeft size={18} className="group-hover:-translate-x-1 transition-transform" />
          <span className="text-sm font-medium">Back</span>
        </button>
      </div>

      <div className="max-w-7xl mx-auto px-6 -mt-12 relative z-10">
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            
            {/* Left Column: Profile Preview Card */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white rounded-3xl shadow-xl shadow-emerald-900/5 border border-slate-100 overflow-hidden">
                <div className="p-8 flex flex-col items-center text-center">
                  <div className="relative mb-6 group">
                    <div className="w-32 h-32 rounded-3xl overflow-hidden ring-4 ring-white shadow-lg shadow-emerald-900/10 bg-slate-50 relative">
                      {imagePreview ? (
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : teacherData?.image ? (
                        <img src={`/${teacherData.image}`} alt="Current" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-emerald-100 to-emerald-50 text-emerald-600">
                          <span className="text-3xl font-black">
                            {formData.name ? formData.name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'T'}
                          </span>
                        </div>
                      )}
                      <label htmlFor="staff-image-edit" className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer">
                        <span className="text-white text-xs font-bold uppercase tracking-widest">Update</span>
                        <input
                          type="file"
                          accept=".png,.jpg,.jpeg"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              setFormData({ ...formData, image: file });
                              const reader = new FileReader();
                              reader.onloadend = () => setImagePreview(reader.result);
                              reader.readAsDataURL(file);
                            }
                          }}
                          className="hidden"
                          id="staff-image-edit"
                        />
                      </label>
                    </div>
                  </div>

                  <h2 className="text-2xl font-bold text-slate-900 mb-1">{formData.name || 'Staff Member'}</h2>
                  <p className="text-emerald-500 font-bold text-[10px] tracking-[0.2em] uppercase mb-6">
                    {formData.designation || 'PENDING ASSIGNMENT'}
                  </p>

                  <div className="w-full pt-6 border-t border-slate-100">
                    <button
                      type="submit"
                      disabled={saving}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 rounded-xl transition-all shadow-lg shadow-blue-600/30 disabled:opacity-50 flex items-center justify-center gap-2 active:scale-95"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          <span>Saving Changes...</span>
                        </>
                      ) : (
                        <span>Save Staff Profile</span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => navigate(`/staff/${id}`)}
                      className="w-full mt-3 bg-white hover:bg-slate-50 text-slate-600 font-bold py-4 rounded-2xl border border-slate-200 transition-all flex items-center justify-center gap-2"
                    >
                      Cancel & Exit
                    </button>
                  </div>
                </div>
              </div>

              {/* Designation Quick Select */}
              <div className="bg-white p-6 rounded-3xl shadow-xl shadow-emerald-900/5 border border-slate-100">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-4">Current Designation</label>
                <select
                  value={formData.designation}
                  onChange={(e) => setFormData({ ...formData, designation: e.target.value, customFields: {} })}
                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700 appearance-none cursor-pointer"
                  required
                >
                  <option value="">-- Select Designation --</option>
                  {Array.isArray(designations) && designations.map((designation) => (
                    <option key={designation._id} value={designation.name}>
                      {designation.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Right Column: Tabbed Form */}
            <div className="lg:col-span-8 space-y-8">
              {/* Tabs Header Removed for Sequential Layout */}

              {/* Tab Content */}
              <div className="bg-white rounded-3xl shadow-xl shadow-emerald-900/5 border border-slate-100 p-8 min-h-[600px] animate-fadeIn">
                
                {/* Mobile Login Credentials (Integrated) - Moved to top */}
                <div className="p-6 bg-emerald-50/50 rounded-[2rem] border border-emerald-100 mb-8">
                  <div className="flex justify-between items-center mb-4">
                    <h4 className="text-sm font-bold text-slate-700 uppercase tracking-wider">Mobile Portal Access</h4>
                    <span className="px-3 py-1 bg-emerald-100 text-emerald-600 rounded-lg text-[10px] font-black uppercase">Required for App</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Portal Login ID</label>
                      <input
                        type="text"
                        value={teacherData.employeeId || 'Auto-generated'}
                        disabled
                        className="w-full px-4 py-3 bg-slate-200/50 border border-slate-200 rounded-xl text-slate-500 font-bold cursor-not-allowed"
                      />
                    </div>
                    <FormField label="Access Password *" error={validationErrors.password}>
                      <input
                        type="text"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Set login password"
                        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 font-mono shadow-sm"
                      />
                    </FormField>
                  </div>
                  <div className="mt-4">
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2 ml-1">Biometric Employee No (Optional)</label>
                    <input
                      type="text"
                      value={formData.employeeNo || ''}
                      onChange={(e) => setFormData({ ...formData, employeeNo: e.target.value })}
                      placeholder="e.g. STAFF001"
                      className="w-full md:w-1/2 px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm"
                    />
                    <p className="mt-1 text-[10px] text-slate-400 ml-1 italic">Map this to the ID on the Hikvision device to sync attendance automatically.</p>
                  </div>
                </div>

                {/* Personal Information Section */}
                <div className="space-y-8">

                    <div className="border-b border-slate-50 pb-4">
                      <h3 className="text-xl font-bold text-slate-900">Personal Information</h3>
                      <p className="text-sm text-slate-500">Identity and contact details for staff member</p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField label="Full Name *" error={validationErrors.name}>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({...formData, name: e.target.value})}
                          placeholder="Enter full name"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-medium text-slate-700"
                        />
                      </FormField>

                      <FormField label="Email Address *" error={validationErrors.email}>
                        <input
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({...formData, email: e.target.value})}
                          placeholder="name@school.com"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-medium text-slate-700"
                        />
                      </FormField>

                      <FormField label="Phone Number *" error={validationErrors.phone}>
                        <PhoneInput
                          value={formData.phone}
                          onChange={(val) => setFormData({ ...formData, phone: val })}
                        />
                      </FormField>



                      <FormField label="Date of Birth *" error={validationErrors.dateOfBirth}>
                        <CustomDatePicker
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={(e) => setFormData({...formData, dateOfBirth: e.target.value})}
                          maxYear={new Date().getFullYear()}
                        />
                      </FormField>

                      <FormField label="Gender">
                        <select
                          value={formData.gender}
                          onChange={(e) => setFormData({...formData, gender: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700 appearance-none cursor-pointer"
                        >
                          <option value="male">Male</option>
                          <option value="female">Female</option>
                          <option value="other">Other</option>
                        </select>
                      </FormField>

                      <FormField label="Marital Status">
                        <select
                          value={formData.maritalStatus}
                          onChange={(e) => setFormData({ ...formData, maritalStatus: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700 appearance-none cursor-pointer"
                        >
                          <option value="single">Single</option>
                          <option value="married">Married</option>
                          <option value="divorced">Divorced</option>
                          <option value="widowed">Widowed</option>
                        </select>
                      </FormField>

                      <FormField label="Mother Tongue">
                        <input
                          type="text"
                          value={formData.motherTongue}
                          onChange={(e) => setFormData({...formData, motherTongue: e.target.value})}
                          placeholder="e.g. English, Hindi"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-medium text-slate-700"
                        />
                      </FormField>

                      <FormField label="Nationality">
                        <input
                          type="text"
                          value={formData.nationality}
                          onChange={(e) => setFormData({...formData, nationality: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-medium text-slate-700"
                        />
                      </FormField>

                      <div className="md:col-span-2">
                        <FormField label="Permanent Address">
                          <textarea
                            value={formData.address}
                            onChange={(e) => setFormData({...formData, address: e.target.value})}
                            placeholder="Complete residential address"
                            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-medium text-slate-700 resize-none"
                            rows={3}
                          />
                        </FormField>
                      </div>
                    </div>

                    {/* Emergency Contacts */}
                    <div className="pt-8 mt-8 border-t border-slate-50">
                      <div className="flex justify-between items-center mb-6">
                        <div>
                          <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest">Emergency Contacts</h4>
                          <p className="text-xs text-slate-500 mt-1">Secondary contact persons for safety</p>
                        </div>
                        <button
                          type="button"
                          onClick={addContactPerson}
                          className="px-4 py-2 bg-emerald-50 text-emerald-600 font-bold text-xs rounded-xl hover:bg-emerald-100 transition-colors"
                        >
                          + Add Contact
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {formData.contactPerson?.map((contact, index) => (
                          <div key={index} className="p-6 bg-slate-50/50 rounded-3xl border border-slate-100 relative group">
                            {formData.contactPerson.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeContactPerson(index)}
                                className="absolute -top-2 -right-2 w-8 h-8 bg-red-50 text-red-500 rounded-full flex items-center justify-center hover:bg-red-500 hover:text-white transition-all shadow-sm opacity-0 group-hover:opacity-100"
                              >
                                <X size={14} />
                              </button>
                            )}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              <FormField label="Full Name">
                                <input
                                  type="text"
                                  value={contact.name}
                                  onChange={(e) => updateContactPerson(index, 'name', e.target.value)}
                                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all"
                                />
                              </FormField>
                              <FormField label="Relationship">
                                <select
                                  value={contact.relation}
                                  onChange={(e) => updateContactPerson(index, 'relation', e.target.value)}
                                  className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700 appearance-none cursor-pointer"
                                >
                                  <option value="">Select Relation</option>
                                  <option value="Father">Father</option>
                                  <option value="Mother">Mother</option>
                                  <option value="Guardian">Guardian</option>
                                  <option value="Sibling">Sibling</option>
                                  <option value="Other">Other</option>
                                </select>
                              </FormField>
                              <FormField label="Contact Phone">
                                <PhoneInput
                                  value={contact.phone}
                                  onChange={(val) => updateContactPerson(index, 'phone', val)}
                                />
                              </FormField>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                {/* Professional Details Section */}
                <div className="space-y-8 mt-12 pt-12 border-t border-slate-200">
                    <div className="border-b border-slate-50 pb-4">
                      <h3 className="text-xl font-bold text-slate-900">Professional Details</h3>
                      <p className="text-sm text-slate-500">Employment history and school assignments</p>
                    </div>


                    {/* Staff Assignment Details */}
                    {designations.find(d => d.name === formData.designation)?.fieldType === 'teaching' && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 bg-slate-50/50 p-6 rounded-[2rem] border border-slate-200/60 mb-2">
                        <FormField label="Academic Year *" error={validationErrors.academicYear}>
                          <select
                            value={formData.academicYear}
                            onChange={(e) => setFormData({ ...formData, academicYear: e.target.value, inChargeOf: '' })}
                            className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700 appearance-none cursor-pointer"
                          >
                            <option value="">Select Year</option>
                            {getUniqueAcademicYears().map(year => (
                              <option key={year} value={year}>{year}</option>
                            ))}
                          </select>
                        </FormField>

                        <FormField label="Grade / Class">
                          <SearchableDropdown
                            options={[...new Set(getFilteredClasses().map(cls => cls.name))].map(name => ({ _id: name, name }))}
                            value={formData.inChargeOf?.name ? { _id: formData.inChargeOf.name, name: formData.inChargeOf.name } : ''}
                            onChange={(selected) => setFormData(prev => ({ ...prev, inChargeOf: { ...prev.inChargeOf, name: selected.name } }))}
                            placeholder={formData.academicYear ? "Select Grade" : "Select Year First"}
                            disabled={!formData.academicYear}
                          />
                        </FormField>

                        <FormField label="Section">
                          <SearchableDropdown
                            options={getFilteredClasses()
                              .filter(cls => cls.name === formData.inChargeOf?.name)
                              .map(cls => ({ ...cls, section: cls.section || 'General' }))}
                            value={formData.inChargeOf?._id ? formData.inChargeOf : ''}
                            onChange={(selectedSection) => setFormData(prev => ({ ...prev, inChargeOf: selectedSection }))}
                            labelKey="section"
                            placeholder={formData.inChargeOf?.name ? "Select Section" : "Select Grade First"}
                            disabled={!formData.inChargeOf?.name}
                          />
                        </FormField>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <FormField label="Qualification">
                        <input
                          type="text"
                          value={formData.qualification}
                          onChange={(e) => setFormData({...formData, qualification: e.target.value})}
                          placeholder="e.g. M.Ed, PhD"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-medium text-slate-700"
                        />
                      </FormField>

                      <FormField label="Experience (Years)">
                        <input
                          type="number"
                          value={formData.experienceInYears}
                          onChange={(e) => setFormData({...formData, experienceInYears: e.target.value})}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-medium text-slate-700"
                        />
                      </FormField>


                      <FormField label="Aadhar Card Number">
                        <input
                          type="text"
                          value={formData.aadharNumber}
                          onChange={(e) => setFormData({ ...formData, aadharNumber: e.target.value })}
                          placeholder="12-digit number"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700"
                        />
                      </FormField>

                      <FormField label="PAN Card Number">
                        <input
                          type="text"
                          value={formData.pan}
                          onChange={(e) => setFormData({ ...formData, pan: e.target.value })}
                          placeholder="ABCDE1234F"
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700 uppercase"
                        />
                      </FormField>

                      <FormField label="Blood Group">
                        <select
                          value={formData.bloodGroup}
                          onChange={(e) => setFormData({ ...formData, bloodGroup: e.target.value })}
                          className="w-full px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700 appearance-none cursor-pointer"
                        >
                          <option value="">Select Group</option>
                          <option value="A+">A+</option>
                          <option value="A-">A-</option>
                          <option value="B+">B+</option>
                          <option value="B-">B-</option>
                          <option value="AB+">AB+</option>
                          <option value="AB-">AB-</option>
                          <option value="O+">O+</option>
                          <option value="O-">O-</option>
                        </select>
                      </FormField>
                    </div>

                    {/* Teaching Assignments (Conditionally shown) */}
                    {designations.find(d => d.name === formData.designation)?.fieldType === 'teaching' && (
                      <div className="pt-8 mt-8 border-t border-slate-50">
                        <div className="flex justify-between items-center mb-6">
                          <h4 className="text-sm font-black text-slate-400 uppercase tracking-widest">Subjects Handled</h4>
                          <button
                            type="button"
                            onClick={addSubject}
                            className="px-4 py-2 bg-blue-50 text-blue-600 font-bold text-xs rounded-xl hover:bg-blue-100 transition-all active:scale-95"
                          >
                            + Add Subject
                          </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {formData.subjects?.map((subject, index) => (
                            <div key={index} className="flex gap-2 group">
                               <input
                                 type="text"
                                 placeholder="e.g. Mathematics"
                                 value={subject.name}
                                 onChange={(e) => updateSubject(index, e.target.value)}
                                 className="flex-1 px-4 py-3 bg-white border border-slate-200 rounded-xl focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all hover:border-slate-300 shadow-sm font-bold text-slate-700"
                                 required
                               />
                              <button
                                type="button"
                                onClick={() => removeSubject(index)}
                                className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all"
                              >
                                <X size={18} />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                {/* Additional Information Section */}
                <div className="space-y-8 mt-12 pt-12 border-t border-slate-200">
                    <div className="border-b border-slate-50 pb-4">
                      <h3 className="text-xl font-bold text-slate-900">Additional Information</h3>
                      <p className="text-sm text-slate-500">Designation-specific data and custom fields</p>
                    </div>

                    {configFields.filter(f => !(SYSTEM_FIELDS.includes(f.fieldName) && f.fieldName !== 'salary')).length > 0 ? (
                      <div className="bg-slate-50/30 p-8 rounded-3xl border border-slate-100">
                        <DynamicFields
                          fields={configFields.filter(f => !(SYSTEM_FIELDS.includes(f.fieldName) && f.fieldName !== 'salary'))}
                          values={{ ...(formData.customFields || {}), ...formData }}
                          onChange={handleCustomFieldChange}
                          errors={validationErrors}
                        />
                      </div>
                    ) : (
                      <div className="h-48 flex items-center justify-center bg-slate-50 rounded-3xl border-2 border-dashed border-slate-200">
                        <p className="text-slate-400 font-medium">No custom fields defined for {formData.designation}</p>
                      </div>
                    )}
                  </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// Internal component for form fields with error handling
const FormField = ({ label, children, error }) => (
  <div className="space-y-1">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] block pl-1">{label}</label>
    <div className="relative">
      {children}
    </div>
    {error && (
      <p className="text-[10px] font-bold text-red-500 pl-1 mt-1 uppercase tracking-wide animate-pulse">
        {error}
      </p>
    )}
  </div>
);

export default StaffEdit;
